import { FC, useState, useCallback, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Typography,
    Box,
    IconButton,
    LinearProgress,
    Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { SCUIButton } from '../../../atomic/atoms';
import AIGeneratorCommandService from '../../../services/studio/StudioCommand/AIGenerator/AIGeneratorCommand.services';
import { resolveAIGenerationError } from '../../../utils/aiGenerationErrorResolver';

interface PromptEnhanceModalProps {
    open: boolean;
    onClose: () => void;
    originalPrompt: string;
    onApply: (enhancedPrompt: string) => void;
}

export const PromptEnhanceModal: FC<PromptEnhanceModalProps> = ({ open, onClose, originalPrompt, onApply }) => {
    const { t } = useTranslation();
    const [enhancedPrompt, setEnhancedPrompt] = useState('');
    const [summary, setSummary] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const abortRef = useRef(false);

    const enhancePrompt = useCallback(
        async (prompt: string) => {
            abortRef.current = false;
            setLoading(true);
            setError('');
            setEnhancedPrompt('');
            setSummary('');
            try {
                const response = await AIGeneratorCommandService.EnhancePrompt(prompt);
                if (abortRef.current) return;
                if (response?.success) {
                    setEnhancedPrompt(response.enhancedPrompt);
                    setSummary(response.summary || '');
                } else {
                    setError(resolveAIGenerationError(response?.errorCode, 'studyoPrompt.enhanceModal.error'));
                }
            } catch {
                if (!abortRef.current) {
                    setError(t('studyoPrompt.enhanceModal.error'));
                }
            } finally {
                if (!abortRef.current) {
                    setLoading(false);
                }
            }
        },
        [t]
    );

    const handleEntered = () => {
        if (originalPrompt?.trim()) {
            enhancePrompt(originalPrompt);
        }
    };

    const handleExited = () => {
        abortRef.current = true;
        setEnhancedPrompt('');
        setSummary('');
        setError('');
        setLoading(false);
    };

    const handleApply = () => {
        if (enhancedPrompt) {
            onApply(enhancedPrompt);
            onClose();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            slotProps={{
                transition: {
                    onEntered: handleEntered,
                    onExited: handleExited,
                },
            }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                <Typography variant="h6" component="span">
                    {t('studyoPrompt.enhanceModal.title')}
                </Typography>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            {loading && <LinearProgress />}

            <DialogContent dividers>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* Original Prompt */}
                    <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                            {t('studyoPrompt.enhanceModal.originalPrompt')}
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            maxRows={8}
                            value={originalPrompt}
                            slotProps={{
                                input: { readOnly: true },
                            }}
                            sx={{
                                '& .MuiInputBase-root': {
                                    backgroundColor: 'action.hover',
                                },
                            }}
                        />
                    </Box>

                    {/* Enhanced Prompt */}
                    <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                            {t('studyoPrompt.enhanceModal.enhancedPrompt')}
                        </Typography>

                        {error && (
                            <Alert severity="error" sx={{ mb: 1 }}>
                                {error}
                            </Alert>
                        )}

                        <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            maxRows={10}
                            value={loading ? '' : enhancedPrompt}
                            onChange={(e) => setEnhancedPrompt(e.target.value)}
                            disabled={loading}
                            placeholder={loading ? t('studyoPrompt.enhanceModal.loading') : ''}
                        />
                    </Box>

                    {/* Summary */}
                    {!loading && summary && (
                        <Alert
                            severity="warning"
                            icon={false}
                            sx={{
                                '& .MuiAlert-message': { width: '100%' },
                            }}
                        >
                            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                                {t('studyoPrompt.enhanceModal.summaryTitle')}
                            </Typography>
                            {summary
                                .split('\n')
                                .filter((line) => line.trim())
                                .map((line) => (
                                    <Typography key={line} variant="body2" sx={{ py: 0.15 }}>
                                        {line}
                                    </Typography>
                                ))}
                        </Alert>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                <SCUIButton
                    text={t('studyoPrompt.enhanceModal.cancel')}
                    type="secondary"
                    size="m"
                    onClick={onClose}
                    sizeType="fixed"
                />
                <SCUIButton
                    text={t('studyoPrompt.enhanceModal.transfer')}
                    type="primary"
                    size="m"
                    onClick={handleApply}
                    disabled={loading || !enhancedPrompt}
                    sizeType="fixed"
                />
            </DialogActions>
        </Dialog>
    );
};
